import { AuthService } from './authService';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://localhost:7141/api/v1';

/**
 * Etterkontroll ved varemottak (Teamwork 36333932): kontrollkø, justering med
 * logging og godkjenning som trigger den holdte ERP-eksporten.
 */
export interface ReceiptReviewDto {
  id: number;
  batchID: string;
  orderNo: number | null;
  orderNoERP: string | null;
  supplierName: string | null;
  status: number; // 0 = Venter, 1 = Godkjent
  receivedBy: string | null;
  approvedBy: string | null;
  approvedAt: string | null;
  note: string | null;
  created: string;
  lineCount: number;
  adjustmentCount: number;
}

export interface ReceiptReviewLineDto {
  lineID: number;
  itemNo: string | null;
  description1: string | null;
  quantity: number;
  itemUnitCode: string | null;
  operatorID: string | null;
  transDt: string | null;
  locationName: string | null;
  itemBatchNo: string | null;
  itemSerialNo: string | null;
  lineStatus: number;
  originalQuantity: number | null;
  orderedQuantity: number | null;
  remainingQuantity: number | null;
}

export interface ReceiptReviewAdjustmentDto {
  id: number;
  stcTransLineID: number;
  itemNo: string | null;
  originalQuantity: number;
  adjustedQuantity: number;
  adjustedBy: string | null;
  adjustedAt: string;
  note: string | null;
}

class ReceiptReviewService {
  async getReviews(includeApproved: boolean = false, warehouseId?: string): Promise<ReceiptReviewDto[]> {
    const params = new URLSearchParams({ includeApproved: String(includeApproved) });
    if (warehouseId) params.append('warehouseId', warehouseId);
    const response = await AuthService.authenticatedGet(
      `${API_BASE_URL}/receipt-review?${params.toString()}`);
    if (!response.ok) throw new Error(`Failed to fetch receipt reviews: ${response.statusText}`);
    return response.json();
  }

  async getLines(reviewId: number): Promise<ReceiptReviewLineDto[]> {
    const response = await AuthService.authenticatedGet(
      `${API_BASE_URL}/receipt-review/${reviewId}/lines`);
    if (!response.ok) throw new Error(`Failed to fetch review lines: ${response.statusText}`);
    return response.json();
  }

  async getAdjustments(reviewId: number): Promise<ReceiptReviewAdjustmentDto[]> {
    const response = await AuthService.authenticatedGet(
      `${API_BASE_URL}/receipt-review/${reviewId}/adjustments`);
    if (!response.ok) throw new Error(`Failed to fetch adjustments: ${response.statusText}`);
    return response.json();
  }

  async adjustLine(reviewId: number, stcTransLineID: number, newQuantity: number, note?: string): Promise<void> {
    const response = await AuthService.authenticatedPut(
      `${API_BASE_URL}/receipt-review/${reviewId}/lines/adjust`,
      { stcTransLineID, newQuantity, note });
    if (!response.ok) {
      const error = await response.json().catch(() => null);
      throw new Error(error?.error || `Failed to adjust line: ${response.statusText}`);
    }
  }

  async approve(reviewId: number, note?: string): Promise<{ success: boolean; message?: string; filesExported: number }> {
    const response = await AuthService.authenticatedPost(
      `${API_BASE_URL}/receipt-review/${reviewId}/approve`,
      { note: note || null });
    const body = await response.json().catch(() => null);
    if (!response.ok) {
      throw new Error(body?.message || `Failed to approve review: ${response.statusText}`);
    }
    return body;
  }
}

export const receiptReviewService = new ReceiptReviewService();
